import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// ----- Blog -----
export const listBlogPosts = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("blog_posts")
    .select("slug,title,excerpt,category,author,cover_image_url,published_at")
    .eq("is_published", true)
    .order("published_at", { ascending: false });
  if (error) throw new Error(error.message);
  return { posts: data ?? [] };
});

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) =>
    z.object({ slug: z.string().min(1).max(200) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { post };
  });

// ----- Testimonials -----
export const listTestimonials = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("testimonials")
    .select("quote,author_name,author_role,company,is_featured")
    .eq("published", true)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return { testimonials: data ?? [] };
});

// ----- Case studies -----
export const listCaseStudies = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("case_studies")
    .select("slug,title,summary,company,industry,savings_amount,savings_percent")
    .eq("published", true)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return { studies: data ?? [] };
});

// ----- Contact -----
const contactSchema = z.object({
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
  phone: z.string().trim().max(60).optional().or(z.literal("")),
  acv_range: z.string().trim().max(50).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
});

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => contactSchema.parse(input))
  .handler(async ({ data }) => {
    const { error } = await supabaseAdmin.from("contact_submissions").insert({
      name: data.name,
      company: data.company,
      email: data.email,
      phone: data.phone || null,
      acv_range: data.acv_range || "",
      message: data.message || null,
    });
    if (error) {
      console.error("Contact submission failed:", error.message);
      throw new Error("We couldn't save your message. Please try again.");
    }

    // Send notification email via Resend (through Lovable connector gateway)
    try {
      const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
      const RESEND_API_KEY = process.env.RESEND_API_KEY;
      if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY is not configured");
      if (!RESEND_API_KEY) throw new Error("RESEND_API_KEY is not configured");

      const escape = (s: string) =>
        s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

      const html = `
        <h2>New contact form submission</h2>
        <p><strong>Name:</strong> ${escape(data.name)}</p>
        <p><strong>Company:</strong> ${escape(data.company)}</p>
        <p><strong>Email:</strong> ${escape(data.email)}</p>
        <p><strong>Phone:</strong> ${escape(data.phone || "—")}</p>
        <p><strong>Message:</strong></p>
        <p>${escape(data.message || "—").replace(/\n/g, "<br>")}</p>
      `;

      const res = await fetch("https://connector-gateway.lovable.dev/resend/emails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${LOVABLE_API_KEY}`,
          "X-Connection-Api-Key": RESEND_API_KEY,
        },
        body: JSON.stringify({
          from: "SaaSed Contact <onboarding@resend.dev>",
          to: ["hello@saased.co"],
          reply_to: data.email,
          subject: `New contact form submission from ${data.name}`,
          html,
        }),
      });

      if (!res.ok) {
        const body = await res.text();
        console.error(`Resend send failed [${res.status}]: ${body}`);
      }
    } catch (e) {
      console.error("Email notification failed:", e);
      // Don't fail the request — submission is saved
    }

    return { ok: true };
  });

// ----- Posts by author -----
export const listPostsByAuthor = createServerFn({ method: "GET" })
  .inputValidator((input: { name: string }) =>
    z.object({ name: z.string().min(1).max(200) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: posts, error } = await supabaseAdmin
      .from("blog_posts")
      .select("slug,title,excerpt,published_at")
      .eq("is_published", true)
      .ilike("author", data.name)
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { posts: posts ?? [] };
  });
